/**
 * Phone validation utilities for fixing common input errors
 * Focus: German phone numbers with duplicate country codes
 */

/**
 * Fixes duplicate German country codes in phone numbers.
 * Handles patterns like +4949, 004949, 4949 followed by mobile prefixes.
 *
 * @param phone - The phone number to validate and fix
 * @returns The corrected phone number
 */
export function fixGermanDuplicateCountryCode(phone: string): string {
  if (!phone) {
    return phone;
  }

  // Remove spaces and common separators for pattern matching
  const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');

  // Pattern for duplicate German country code followed by mobile prefixes
  // Matches: +4949, 004949, 4949 followed by 15x, 16x, 17x (German mobile prefixes)
  const DUPLICATE_COUNTRY_CODE_PATTERN = /^(\+?0{0,2})?4949(1[5-7]\d)/;

  if (DUPLICATE_COUNTRY_CODE_PATTERN.test(cleanPhone)) {
    // Remove the duplicate 49 country code
    const fixedPhone = cleanPhone.replace(/^(\+?0{0,2})?4949/, '+49');

    // Preserve original formatting if the input had spaces
    if (phone.includes(' ')) {
      // Re-add spaces in common German phone format
      return formatGermanPhone(fixedPhone);
    }

    return fixedPhone;
  }

  // Also handle 00 international prefix conversion for consistency
  if (cleanPhone.startsWith('0049')) {
    const fixedPhone = cleanPhone.replace(/^0049/, '+49');
    if (phone.includes(' ')) {
      return formatGermanPhone(fixedPhone);
    }
    return fixedPhone;
  }

  return phone;
}

/**
 * Determines if a phone number is a German mobile number.
 * Mobile numbers in Germany start with prefixes 15x, 16x, 17x.
 *
 * @param phone - The phone number to check
 * @returns True if the number is a German mobile number
 */
export function isGermanMobileNumber(phone: string): boolean {
  if (!phone) {
    return false;
  }

  // First fix any duplicate country codes
  const fixedPhone = fixGermanDuplicateCountryCode(phone);

  // Remove spaces and separators for pattern matching
  const cleanPhone = fixedPhone.replace(/[\s\-\(\)\.]/g, '');

  // German mobile number pattern (with or without country code)
  // Matches: +4915x, +4916x, +4917x or 015x, 016x, 017x
  const GERMAN_MOBILE_PATTERN = /^(\+?49|0)(1[5-7]\d)/;

  return GERMAN_MOBILE_PATTERN.test(cleanPhone);
}

/**
 * Validates and processes a German phone number.
 * Fixes duplicate country codes and determines the appropriate field.
 *
 * @param phone - The phone number to process
 * @param locale - The locale to check if German processing should apply
 * @returns Object with processed phone and field recommendation
 */
export function processGermanPhoneNumber(
  phone: string,
  locale?: string
): { phone: string; isMobile: boolean; shouldProcess: boolean } {
  // Only process for German locales
  const shouldProcess = locale && (locale.startsWith('de_') || locale === 'de');

  if (!shouldProcess || !phone) {
    return { phone, isMobile: false, shouldProcess: false };
  }

  const fixedPhone = fixGermanDuplicateCountryCode(phone);
  const isMobile = isGermanMobileNumber(fixedPhone);

  return {
    phone: fixedPhone,
    isMobile,
    shouldProcess: true
  };
}

/**
 * Helper function to format German phone numbers with spaces.
 * Adds spaces in common German phone format.
 *
 * @param phone - The phone number to format
 * @returns Formatted phone number with spaces
 */
function formatGermanPhone(phone: string): string {
  // Remove existing spaces first
  const clean = phone.replace(/\s/g, '');

  // Format: +49 xxx xxxxxxx for mobile or +49 xxxx xxxxxx for landline
  if (clean.startsWith('+49')) {
    const withoutCountry = clean.substring(3);

    // Mobile number formatting (15x, 16x, 17x)
    if (/^1[5-7]\d/.test(withoutCountry)) {
      // +49 1xx xxxxxxx
      const prefix = withoutCountry.substring(0, 3);
      const rest = withoutCountry.substring(3);
      return `+49 ${prefix} ${rest}`;
    }

    // Landline formatting
    // This is a simplified version - actual German landline formatting varies by area
    return clean;
  }

  return phone;
}
